import type { AssetsPageViewModel } from 'src/pages/assets-page/assets-page-view-model.js';
import type { State } from 'src/store/types.js';
import { key } from 'src/store/selectors/selectors';
import { variantAssetsView, variantSimplifiedAssets } from 'src/store/selectors/variants.js';
import { entityNameForLists } from 'src/utilities/text';
import { LocalDate } from 'src/utilities/local-date.js';
import { t as tr } from 'src/i18n';
import type { PageLinks } from 'src/pages/overview-page/overview-page-view-model';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import type { AssetViewModelWithEvents } from '../../store/selectors/assets/asset-view-model-with-events';
import { assetsNotDiscardedWithTasks } from '../../store/selectors/assets/assets-not-discarded-with-tasks';
import { assetsDiscardedWithTasks } from '../../store/selectors/assets/assets-discarded-with-tasks';
import { getOrganizationId } from '../../store/selectors/organization/getOrganizationId';
import type { TableItemsAssets } from '../../pages/assets-page/table-items-assets';
import type { Properties } from '../../pages/assets-page/properties';
import { BASE_PATH } from '../../store';
import { getPage } from '../../store/selectors/pages/getPage';
import { getPages } from '../../store/selectors/pages/getPages';

function toTableItemsAssets(
  hrefPrefix: string,
  item: AssetViewModelWithEvents,
  availableCategories: string[],
): TableItemsAssets {
  let href = hrefPrefix + 'assets/' + item.uuid;
  if (!item.isConfirmedEntity) {
    href += '?edit';
  }
  const classes = (status: 'NONE' | 'OK' | 'NOT_OK' | 'DONE') => {
    if (status === 'OK') {
      return 'prev-next asset-ok';
    }
    if (status === 'NOT_OK') {
      return 'prev-next asset-not-ok';
    }
    if (status === 'DONE') {
      return 'prev-next event-done';
    }
    return 'prev-next';
  };
  const result = {
    uuid: item.uuid,
    href,
    name: entityNameForLists(item.name, item.draftName, item.isConfirmedEntity),
    number: item.number ?? '',
    category:
      item.category && !availableCategories.includes(item.category) ? tr('assets.other') : (item.category ?? ''),
    location: item.location ?? '',
    registered: item.createdDateTime ? LocalDate.fromString(item.createdDateTime).toStringForDisplay() : '',
    registeredSortValue: item.createdDateTime ?? '',
    requiresElectricity: item.requiresElectricity ?? false,
    hasDraft: item.hasDraft,
    tasksAndEvents: item.events.map((t) => {
      const properties: Properties[] = [];
      if (t.prev !== undefined) {
        properties.push({
          name: t.prev.dateTimeDisplay,
          label: tr('assets.previous'),
          field: '',
          href: t.prev.href !== '' ? hrefPrefix + t.prev.href : '',
          value: t.prev.dateTimeDisplay,
          classes: classes(t.prev.status),
        });
      } else {
        properties.push({
          name: tr('assets.none'),
          label: tr('assets.previous'),
          field: '',
          href: '',
          value: tr('assets.none'),
          classes: 'prev-next',
        });
      }
      if (t.next !== undefined) {
        properties.push({
          name: t.next.dateTimeDisplay,
          label: tr('assets.next'),
          field: '',
          href: t.next.href !== '' ? hrefPrefix + t.next.href : '',
          value: t.next.dateTimeDisplay,
          classes: classes(t.next.status),
        });
      } else {
        properties.push({
          name: tr('assets.none'),
          label: tr('assets.next'),
          field: '',
          href: '',
          value: tr('assets.none'),
          classes: 'prev-next',
        });
      }
      return {
        classes: '',
        name: t.name,
        href: '',
        properties: properties,
      };
    }),
  };
  if (item.maintenanceRequired && result.tasksAndEvents.length === 0) {
    result.tasksAndEvents = [
      {
        classes: 'alert',
        name: tr('assets.missingMaintenanceTask'),
        href: '',
        properties: [],
      },
    ];
  }
  return result;
}

function hasCheckedEvents(assets: TableItemsAssets[]): boolean {
  return assets.some((asset) =>
    asset.tasksAndEvents.some((event) =>
      event.properties.some(
        (prop) =>
          prop.classes.includes('event-done') ||
          prop.classes.includes('asset-ok') ||
          prop.classes.includes('asset-not-ok'),
      ),
    ),
  );
}

export function assetsPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): AssetsPageViewModel {
  const linkedPage = getPage(355, getPages(state));
  const pageLinks: PageLinks = {
    type: 'page-links',
    items: [
      {
        name: tr('assets.goToThemePage', { name: linkedPage?.name }) || '',
        type: '',
        href: '/account/' + getOrganizationId(state) + '/' + 355,
      },
    ],
  };
  const assetCategories = variantAssetsView(state).categories;
  const assetsNotDiscarded = assetsNotDiscardedWithTasks(state).map((i) =>
    toTableItemsAssets(hrefPrefix, i, assetCategories),
  );
  const assetsDiscarded = assetsDiscardedWithTasks(state).map((i) =>
    toTableItemsAssets(hrefPrefix, i, assetCategories),
  );
  return {
    ...viewModel,
    type: 'assets-page',
    href: hrefPrefix,
    icon: 'assets',
    key: key(state),
    assetsNotDiscarded,
    assetsDiscarded,
    assetsNotDiscardedHasCheckedEvents: hasCheckedEvents(assetsNotDiscarded),
    assetsDiscardedHasCheckedEvents: hasCheckedEvents(assetsDiscarded),
    variantSimplifiedAssets: variantSimplifiedAssets(state),
    pdfHref: BASE_PATH + '/organizations/' + getOrganizationId(state) + '/assets/assets-list.pdf?key=' + key(state),
    pageLinks,
  };
}
